import React, { useState, useEffect, useRef, useCallback } from 'react';
import io from 'socket.io-client';
import styles from './GraspDetectionOverlay.module.css';

const GraspDetectionOverlay = ({ isActive = true, position = 'bottom-right' }) => {
  // State for detection data
  const [detectionResult, setDetectionResult] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [isDetecting, setIsDetecting] = useState(false);
  const [videoFrame, setVideoFrame] = useState(null); // Video frame from WebSocket
  const [annotatedFrame, setAnnotatedFrame] = useState(null); // Annotated frame from detection
  const socketRef = useRef(null);
  const wsRef = useRef(null); // WebSocket ref for video stream
  const videoReconnectTimeoutRef = useRef(null);
  const pingTimeoutRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Handle detection start
  const handleStartDetection = useCallback(() => {
    if (socketRef.current && !isDetecting) {
      socketRef.current.emit('start_detection');
      setIsDetecting(true);
    }
  }, [isDetecting]);

  // Handle detection end
  const handleEndDetection = useCallback(() => {
    if (socketRef.current && !isDetecting) {
      socketRef.current.emit('end_detection');
      setIsDetecting(true);
    }
  }, [isDetecting]);

  // Connect to local WebSocket video stream (port 8001)
  useEffect(() => {
    if (!isActive) return;

    const connectVideoWebSocket = () => {
      // Clean up any existing connection first
      if (wsRef.current && wsRef.current.readyState !== WebSocket.CLOSED) {
        console.log('Closing existing video WebSocket connection');
        wsRef.current.close();
      }
      
      // Clear any pending timeouts
      if (videoReconnectTimeoutRef.current) {
        clearTimeout(videoReconnectTimeoutRef.current);
        videoReconnectTimeoutRef.current = null;
      }
      if (pingTimeoutRef.current) {
        clearTimeout(pingTimeoutRef.current);
        pingTimeoutRef.current = null;
      }
      
      try {
        // Connect to local WebSocket server
        wsRef.current = new WebSocket('wss://camera2.testprismaxteleoprobotserver.org');
        
        wsRef.current.onopen = () => {
          console.log('✅ Connected to local video WebSocket');
          // Send initial ping
          wsRef.current.send(JSON.stringify({type: 'ping'}));
        };
        
        wsRef.current.onmessage = (event) => {
          try {
            const data = JSON.parse(event.data);
            
            if (data.type === 'video_frame') {
              // Update video frame
              setVideoFrame(`data:image/jpeg;base64,${data.data}`);
            } else if (data.type === 'pong') {
              // Clear existing ping timeout
              if (pingTimeoutRef.current) {
                clearTimeout(pingTimeoutRef.current);
              }
              // Send ping after receiving pong
              pingTimeoutRef.current = setTimeout(() => {
                if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
                  wsRef.current.send(JSON.stringify({type: 'ping'}));
                }
              }, 5000); // Send ping every 5 seconds
            }
          } catch (error) {
            console.error('Error parsing WebSocket message:', error);
          }
        };
        
        wsRef.current.onerror = (error) => {
          console.error('WebSocket error:', error);
        };
        
        wsRef.current.onclose = () => {
          console.log('WebSocket closed, reconnecting in 3s...');
          setVideoFrame(null);
          // Clear existing reconnect timeout
          if (videoReconnectTimeoutRef.current) {
            clearTimeout(videoReconnectTimeoutRef.current);
          }
          videoReconnectTimeoutRef.current = setTimeout(connectVideoWebSocket, 3000);
        };
      } catch (error) {
        console.error('Failed to connect WebSocket:', error);
        // Clear existing reconnect timeout
        if (videoReconnectTimeoutRef.current) {
          clearTimeout(videoReconnectTimeoutRef.current);
        }
        videoReconnectTimeoutRef.current = setTimeout(connectVideoWebSocket, 3000);
      }
    };

    connectVideoWebSocket();

    // Cleanup WebSocket
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
      if (videoReconnectTimeoutRef.current) {
        clearTimeout(videoReconnectTimeoutRef.current);
      }
      if (pingTimeoutRef.current) {
        clearTimeout(pingTimeoutRef.current);
      }
    };
  }, [isActive]);

  // Connect to Python detection service
  useEffect(() => {
    if (!isActive) return;

    const connectSocket = () => {
      // Clean up any existing connection first
      if (socketRef.current) {
        console.log('Cleaning up existing socket connection');
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      
      // Clear any pending reconnect timeout
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
      
      // Connect to local Python service
      socketRef.current = io('http://localhost:5001', {
        transports: ['websocket'],
        reconnection: true,
        reconnectionAttempts: 5,
        reconnectionDelay: 1000,
      });

      socketRef.current.on('connect', () => {
        console.log('✅ Connected to detection service');
        setIsConnected(true);
      });

      socketRef.current.on('disconnect', () => {
        console.log('❌ Disconnected from detection service');
        setIsConnected(false);
      });

      socketRef.current.on('detection_started', () => {
        console.log('🚀 Detection started');
      });

      socketRef.current.on('detection_result', (data) => {
        console.log('📊 Received detection result:', data);
        setDetectionResult(data);
        if (data.annotated_frame) {
          setAnnotatedFrame(data.annotated_frame);
        }
        setIsDetecting(false);
      });

      socketRef.current.on('error', (error) => {
        console.error('Error:', error);
        setIsDetecting(false);
      });

      socketRef.current.on('connect_error', (error) => {
        console.error('Connection error:', error.message);
        setIsConnected(false);
      });
    };

    connectSocket();

    // Cleanup
    return () => {
      if (socketRef.current) {
        socketRef.current.removeAllListeners();
        socketRef.current.disconnect();
        socketRef.current = null;
      }
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }
    };
  }, [isActive]);


  // Position classes
  const positionClass = {
    'top-left': styles.topLeft,
    'top-right': styles.topRight,
    'bottom-left': styles.bottomLeft,
    'bottom-right': styles.bottomRight,
  }[position] || styles.bottomRight;

  if (!isActive) return null;

  return (
    <div className={`${styles.overlay} ${positionClass}`}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.title}>
          <span className={`${styles.statusDot} ${isConnected ? styles.connected : styles.disconnected}`}></span>
          <span>Grasp Detection V2</span>
        </div>
      </div>

      {/* Content */}
      <div className={styles.content}>
        {/* Control Buttons */}
        <div className={styles.controlButtons}>
          <button 
            className={styles.detectionBtn}
            onClick={handleStartDetection}
            disabled={isDetecting}
          >
            {isDetecting ? 'Detecting...' : 'Start'}
          </button>
          <button 
            className={styles.detectionBtn}
            onClick={handleEndDetection}
            disabled={isDetecting}
          >
            End
          </button>
        </div>

        {/* Detection Results */}
        {detectionResult && (
          <div className={styles.results}>
            <div className={styles.zones}>
              <div className={styles.zone}>
                <div className={styles.zoneLabel}>Left Zone</div>
                <div className={styles.zoneCount}>{detectionResult.final_left_count || 0}</div>
              </div>
              <div className={styles.zone}>
                <div className={styles.zoneLabel}>Right Zone</div>
                <div className={styles.zoneCount}>{detectionResult.final_right_count || 0}</div>
              </div>
            </div>
          </div>
        )}

        {/* Video Stream with zones and detection boxes */}
        {videoFrame && (
            <div className={styles.debugVideo}>
              <div style={{position: 'relative', width: '100%'}}>
                <img
                  src={videoFrame}
                  alt="Live Video Stream"
                  className={styles.debugImage}
                  style={{width: '100%', height: 'auto', display: 'block'}}
                />
                
                {/* Draw zone boundaries */}
                {detectionResult?.zones && (
                  <>
                    {/* Left zone - rectangular (green) */}
                    <div style={{
                      position: 'absolute',
                      left: `${(detectionResult.zones.left.x1 / 640) * 100}%`,
                      top: `${(detectionResult.zones.left.y1 / 480) * 100}%`,
                      width: `${((detectionResult.zones.left.x2 - detectionResult.zones.left.x1) / 640) * 100}%`,
                      height: `${((detectionResult.zones.left.y2 - detectionResult.zones.left.y1) / 480) * 100}%`,
                      border: '2px solid #10b981',
                      pointerEvents: 'none',
                      opacity: 0.8
                    }} />
                    
                    {/* Right zone - circular (blue) */}
                    <div style={{
                      position: 'absolute',
                      left: `${((detectionResult.zones.right.center_x - detectionResult.zones.right.radius) / 640) * 100}%`,
                      top: `${((detectionResult.zones.right.center_y - detectionResult.zones.right.radius) / 480) * 100}%`,
                      width: `${((detectionResult.zones.right.radius * 2) / 640) * 100}%`,
                      height: `${((detectionResult.zones.right.radius * 2) / 480) * 100}%`,
                      border: '2px solid #3b82f6',
                      borderRadius: '50%',
                      pointerEvents: 'none',
                      opacity: 0.8
                    }} />
                  </>
                )}
              </div>
              
            </div>
        )}
        
        {/* Show annotated frame from detection */}
        {annotatedFrame && (
          <div className={styles.debugVideo}>
            <img
              src={`data:image/jpeg;base64,${annotatedFrame}`}
              alt="Detection Result"
              className={styles.debugImage}
            />
          </div>
        )}
      </div>
    </div>
  );
};


export default GraspDetectionOverlay;